import { useEffect, useState } from 'react'
import type { Book, DownloadFormat } from '../../shared/types'
import { CATALOGUE } from '../../shared/catalogue'
import { requestDownload } from '../lib/api'

interface DownloadDialogProps {
  book: Book | 'all' | null
  initialFormat: DownloadFormat
  onClose: () => void
}

function maskPhone(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 2) return digits.length ? `(${digits}` : ''
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

interface BatchItemStatus {
  id: number
  title: string
  status: 'pending' | 'downloading' | 'done' | 'unavailable' | 'error'
  message?: string
}

export function DownloadDialog({ book, initialFormat, onClose }: DownloadDialogProps) {
  const [format, setFormat] = useState<DownloadFormat>('pdf')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [isBusinessOwner, setIsBusinessOwner] = useState<boolean | null>(null)
  const [optInNewsletter, setOptInNewsletter] = useState(true)
  const [consent, setConsent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [batchItems, setBatchItems] = useState<BatchItemStatus[] | null>(null)
  const [batchCompleted, setBatchCompleted] = useState(false)
  const [error, setError] = useState('')

  const isBatch = book === 'all'

  useEffect(() => {
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose()
    }
    window.addEventListener('keydown', closeWithEscape)
    return () => window.removeEventListener('keydown', closeWithEscape)
  }, [isSubmitting, onClose])

  useEffect(() => {
    if (book) {
      setFormat(initialFormat)
      setError('')
      setBatchItems(null)
      setBatchCompleted(false)
    }
  }, [book, initialFormat])

  if (!book) return null

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')

    if (isBusinessOwner === null) {
      setError('Por favor, informe se você é empresário(a).')
      return
    }

    const digits = phone.replace(/\D/g, '')
    if (digits.length < 10) {
      setError('Informe um telefone válido com DDD (mínimo 10 dígitos).')
      return
    }

    if (isBatch) {
      const items: BatchItemStatus[] = CATALOGUE.map((b) => ({
        id: b.id,
        title: b.title,
        status: 'pending',
      }))
      setBatchItems(items)
      setIsSubmitting(true)

      for (let i = 0; i < CATALOGUE.length; i++) {
        const b = CATALOGUE[i]
        setBatchItems((prev) =>
          prev ? prev.map((item) => (item.id === b.id ? { ...item, status: 'downloading' } : item)) : null
        )

        try {
          const { blob, filename } = await requestDownload({
            bookId: b.id,
            format,
            name,
            email,
            phone,
            isBusinessOwner,
            optInNewsletter,
            consent,
          })
          const objectUrl = URL.createObjectURL(blob)
          const anchor = document.createElement('a')
          anchor.href = objectUrl
          anchor.download = filename
          document.body.append(anchor)
          anchor.click()
          anchor.remove()
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1500)

          setBatchItems((prev) =>
            prev ? prev.map((item) => (item.id === b.id ? { ...item, status: 'done' } : item)) : null
          )
        } catch (reason) {
          const msg = reason instanceof Error ? reason.message : 'Erro'
          const isUnavail = msg.includes('não disponível') || msg.includes('404')
          setBatchItems((prev) =>
            prev
              ? prev.map((item) =>
                  item.id === b.id
                    ? { ...item, status: isUnavail ? 'unavailable' : 'error', message: isUnavail ? 'Em breve' : msg }
                    : item
                )
              : null
          )
        }

        if (i < CATALOGUE.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 650))
        }
      }

      setIsSubmitting(false)
      setBatchCompleted(true)
    } else {
      setIsSubmitting(true)

      try {
        const { blob, filename } = await requestDownload({
          bookId: book.id,
          format,
          name,
          email,
          phone,
          isBusinessOwner,
          optInNewsletter,
          consent,
        })
        const objectUrl = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = filename
        document.body.append(anchor)
        anchor.click()
        anchor.remove()
        URL.revokeObjectURL(objectUrl)
        onClose()
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : 'Não foi possível iniciar o download.')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const dialogTitle = isBatch ? 'Coleção Completa (6 Obras)' : book.title
  const dialogDescription = isBatch
    ? 'Informe seus dados uma única vez para baixar individualmente as 6 obras digitais da coleção.'
    : 'Informe seus dados para receber o arquivo digital gratuitamente.'

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={!isSubmitting ? onClose : undefined}>
      <section
        className="download-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="dialog-close" type="button" aria-label="Fechar" onClick={onClose} disabled={isSubmitting}>
          ×
        </button>
        <span className="eyebrow">{isBatch ? 'Download da coleção' : 'Download da obra'}</span>
        <h2 id="download-title">{dialogTitle}</h2>
        <p className="dialog-description">{dialogDescription}</p>

        {batchItems !== null ? (
          <div className="batch-progress-view">
            <div className="batch-header">
              <span className="batch-progress-text">
                {batchCompleted
                  ? 'Processo concluído!'
                  : 'Transferindo os 6 arquivos individualmente para o seu navegador...'}
              </span>
            </div>
            <div className="batch-list">
              {batchItems.map((item, idx) => (
                <div key={item.id} className={`batch-item status-${item.status}`}>
                  <div className="batch-item-info">
                    <span className="batch-item-num">Obra {String(idx + 1).padStart(2, '0')}</span>
                    <strong className="batch-item-title">{item.title}</strong>
                  </div>
                  <span className={`batch-badge badge-${item.status}`}>
                    {item.status === 'pending' && 'Aguardando'}
                    {item.status === 'downloading' && 'Baixando...'}
                    {item.status === 'done' && '✓ Baixado'}
                    {item.status === 'unavailable' && (item.message ?? 'Em breve')}
                    {item.status === 'error' && (item.message ?? 'Erro')}
                  </span>
                </div>
              ))}
            </div>
            {batchCompleted ? (
              <button className="button button-primary button-full" type="button" onClick={onClose}>
                Concluir e fechar
              </button>
            ) : (
              <p className="batch-tip">
                Por favor, mantenha esta janela aberta enquanto os downloads são concluídos.
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <fieldset className="format-options">
              <legend>Formato dos arquivos</legend>
              <label>
                <input type="radio" name="format" value="pdf" checked={format === 'pdf'} onChange={() => setFormat('pdf')} />
                <span><strong>PDF</strong> Leitura e impressão</span>
              </label>
              <label>
                <input type="radio" name="format" value="epub" checked={format === 'epub'} onChange={() => setFormat('epub')} />
                <span><strong>EPUB</strong> Leitura digital</span>
              </label>
            </fieldset>
            <div className="form-grid">
              <label className="field">
                <span>Nome completo</span>
                <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" minLength={2} maxLength={120} placeholder="Seu nome" required />
              </label>
              <label className="field">
                <span>E-mail</span>
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" maxLength={254} placeholder="seu.email@exemplo.com" required />
              </label>
              <label className="field">
                <span>Telefone / WhatsApp</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(maskPhone(event.target.value))}
                  autoComplete="tel"
                  placeholder="(62) 99999-9999"
                  required
                />
              </label>
              <fieldset className="choice-fieldset">
                <legend>Você é empresário(a)?</legend>
                <div className="choice-row">
                  <label className={`choice-pill ${isBusinessOwner === true ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="isBusinessOwner"
                      checked={isBusinessOwner === true}
                      onChange={() => setIsBusinessOwner(true)}
                      required
                    />
                    <span>Sim</span>
                  </label>
                  <label className={`choice-pill ${isBusinessOwner === false ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="isBusinessOwner"
                      checked={isBusinessOwner === false}
                      onChange={() => setIsBusinessOwner(false)}
                      required
                    />
                    <span>Não</span>
                  </label>
                </div>
              </fieldset>
            </div>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={optInNewsletter}
                onChange={(event) => setOptInNewsletter(event.target.checked)}
              />
              <span>Tenho interesse em receber informativos, cursos e novidades do Sistema Fecomércio/Sesc/Senac Goiás.</span>
            </label>
            <label className="consent">
              <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} required />
              <span>
                Autorizo o registro dos meus dados (nome, e-mail e telefone) pelo Sistema Fecomércio Goiás, protegidos conforme a LGPD (Lei nº 13.709/18), para fins de acesso à coleção.
              </span>
            </label>
            {error && <p className="form-error" role="alert">{error}</p>}
            <button className="button button-primary button-full" type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Preparando arquivos...'
                : isBatch
                  ? `Baixar Coleção Completa (${format.toUpperCase()})`
                  : `Baixar em ${format.toUpperCase()}`}
            </button>
          </form>
        )}
      </section>
    </div>
  )
}
